import { logger } from "./logger";
/**
 * Avatar caching utilities to reduce external image requests
 */

import { env } from "../config/env";

interface CachedAvatar {
  url: string;
  timestamp: number;
  blob?: Blob;
}

class AvatarCache {
  private cache = new Map<string, CachedAvatar>();
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private readonly MAX_CACHE_SIZE = 50; // Maximum number of cached avatars

  /**
   * Resolve avatar URL - prepend API base URL for relative paths
   */
  private resolveAvatarUrl(url: string): string {
    if (!url) return url;

    // If it's a relative API path, prepend the API base URL
    if (url.startsWith("/api/")) {
      return `${env.API_BASE_URL}${url}`;
    }

    return url;
  }

  /**
   * Get cached avatar or fetch and cache it
   */
  async getAvatar(userId: string, originalUrl: string): Promise<string> {
    // Resolve the URL first
    const resolvedUrl = this.resolveAvatarUrl(originalUrl);

    // Check if we have a cached version
    const cached = this.cache.get(userId);

    if (cached && this.isValidCache(cached)) {
      if (cached.blob) {
        return URL.createObjectURL(cached.blob);
      }
      return cached.url;
    }

    // If it's already a local URL (from our backend), use it directly
    if (this.isLocalUrl(resolvedUrl)) {
      this.setCacheEntry(userId, resolvedUrl);
      return resolvedUrl;
    }

    // For external URLs (like Google), try to cache the image
    try {
      const blob = await this.fetchImageBlob(originalUrl);
      const objectUrl = URL.createObjectURL(blob);

      this.setCacheEntry(userId, originalUrl, blob);
      return objectUrl;
    } catch (error) {
      logger.warn("Failed to cache avatar:", error);
      // Fallback to original URL
      this.setCacheEntry(userId, originalUrl);
      return originalUrl;
    }
  }

  /**
   * Check if cached entry is still valid
   */
  private isValidCache(cached: CachedAvatar): boolean {
    return Date.now() - cached.timestamp < this.CACHE_DURATION;
  }

  /**
   * Check if URL is from our own backend (doesn't need caching)
   */
  private isLocalUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const currentHost = window.location.hostname;
      const apiHost = new URL(env.API_BASE_URL).hostname;

      // Check if it's the same domain, API domain, or a relative URL
      return (
        urlObj.hostname === currentHost ||
        urlObj.hostname === apiHost ||
        url.startsWith("./") ||
        !url.includes("://")
      );
    } catch {
      // If URL parsing fails, assume it's relative/local
      return true;
    }
  }

  /**
   * Fetch image as blob for caching
   */
  private async fetchImageBlob(url: string): Promise<Blob> {
    const response = await fetch(url, {
      mode: "cors",
      cache: "force-cache",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Set cache entry and manage cache size
   */
  private setCacheEntry(userId: string, url: string, blob?: Blob): void {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        const oldEntry = this.cache.get(oldestKey);

        // Revoke object URL to prevent memory leaks
        if (oldEntry?.blob) {
          URL.revokeObjectURL(URL.createObjectURL(oldEntry.blob));
        }

        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(userId, {
      url,
      timestamp: Date.now(),
      blob,
    });
  }

  /**
   * Clear expired cache entries
   */
  clearExpired(): void {
    const now = Date.now();

    for (const [key, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.CACHE_DURATION) {
        // Revoke object URL to prevent memory leaks
        if (cached.blob) {
          URL.revokeObjectURL(URL.createObjectURL(cached.blob));
        }
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    // Revoke all object URLs to prevent memory leaks
    for (const cached of this.cache.values()) {
      if (cached.blob) {
        URL.revokeObjectURL(URL.createObjectURL(cached.blob));
      }
    }

    this.cache.clear();
  }

  /**
   * Preload avatar for better UX
   */
  async preloadAvatar(userId: string, url?: string): Promise<void> {
    if (!url) return;

    try {
      await this.getAvatar(userId, url);
    } catch (error) {
      logger.warn("Failed to preload avatar:", error);
    }
  }
}

// Export singleton instance
export const avatarCache = new AvatarCache();

// Clean up expired cache entries periodically
setInterval(
  () => {
    avatarCache.clearExpired();
  },
  60 * 60 * 1000,
); // Every hour
